import React, { useState, useEffect } from "react";
import "./styles.css";

const CustomAccordionContext = React.createContext(null);

/**
 * @name CustomAccordion
 * @function
 * @param {BOOLEAN} isOpen - Determines if the accordion should be open by default
 * @param {STRING} activeClass - Additional class that gets added when the accordion is open
 * @param {STRING} className - Additional classes to add to the custom accordion element
 * @param {STRING} data-point - Data point that should be applied to the CustomAccordion
 * @extends React.Component
 * @exported true
 * @description The main component for a custom accordion
 * @notes *isOpen* property by default is set to false
 * @example
 * export deafult function Component(props) {
 *  return (
 *      <div>
 *          <CustomAccordion isOpen={ true }> //isOpen is an optional property
 *              ...
 *          </CustomAccordion>
 *      </div>
 *  );
 * }
 */
export default function CustomAccordion(props) {
    const active = props.activeClass ?? "";
    const className = props.className ?? "";
    const point =props["data-point"] ?props["data-point"] : props.dataPoint ?? "";

    const [ state, setState ] = useState(props.isOpen ?? false);
    const controller = {
        "state" : state,
        "toggle" : () => setState(!state),
        "close" : () => setState(false),
        "open" : () => setState(true)
    };

    useEffect(() => setState(props.isOpen ?? false), [ props.isOpen ]);

    return (
        <CustomAccordionContext.Provider value={ controller }>
            <div className={ `custom-accordion ${ state ? active : "" } ${ className }` } data-point={ point }>
                { props.children }
            </div>
        </CustomAccordionContext.Provider>
    );
}

export const useCustomAccordion = () => React.useContext(CustomAccordionContext);

/**
 * @name CustomAccordionToggle
 * @function
 * @param {STRING} className - Additional classes to add to the accordion toggle element
 * @extends React.Component
 * @exported true
 * @description Wrapper component to toggle the accordion content on click
 * @example
 * export deafult function Component(props) {
 *  return (
 *      <div>
 *          <CustomAccordion>
 *              <CustomAccordionToggle>
 *                  <SVGAsset id="OpenIcon" />
 *              </CustomAccordionToggle>
 * 
 *              ...
 *          </CustomAccordion>
 *      </div>
 *  );
 * }
 */
export function CustomAccordionToggle(props) {
    const controller = useCustomAccordion();
    const onClick = () => {
        controller.toggle();

        if(props.onClick instanceof Function) {
            props.onClick(!controller.state);
        }
    };

    if(!controller) {
        throw new Error('Unable to access "useCustomAccordion" hook. CustomAccordionToggle must be a child of CustomAccordion');
    }

    return (
        <div className={ `custom-accordion-toggle clickable ${ props.className ?? "" }` } onClick={ onClick }>
            { props.children }
        </div>
    );
}

/**
 * @name CustomAccordionContent
 * @function
 * @param {STRING} className - Additional classes to add to the accordion content element
 * @extends React.Component
 * @exported true
 * @description Content wrapper for the accordion, shows/hides based on state
 * @example
 * export deafult function Component(props) {
 *  return (
 *      <div>
 *          <CustomAccordion>
 *              <CustomAccordionToggle>
 *                  <SVGAsset id="OpenIcon" />
 *              </CustomAccordionToggle>
 * 
 *              <CustomAccordionContent>
 *                  ...
 *              </CustomAccordionContent>
 *          </CustomAccordion>
 *      </div>
 *  );
 * }
 */
export function CustomAccordionContent(props) {
    const controller = useCustomAccordion();

    if(!controller) {
        throw new Error('Unable to access "useCustomAccordion" hook. CustomAccoridionContent must be a child of CustomAccordion');
    }

    return (
        <div className={ `custom-accordion-content ${ props.className ?? "" } ${ !controller.state && "custom-accordion-content-hidden" }` }>
            { props.children }
        </div>
    );
}

/**
 * @name CustomAccordionClose
 * @function
 * @param {STRING} className - Additional classes to add on to the accordion close element
 * @extends React.Component
 * @exported true
 * @description Wrapper to close the accordion on click
 * @example
 * export deafult function Component(props) {
 *  return (
 *      <div>
 *          <CustomAccordion>
 *              <CustomAccordionToggle>
 *                  <SVGAsset id="OpenIcon" />
 *              </CustomAccordionToggle>
 * 
 *              <CustomAccordionContent>
 *                  <CustomAccordionClose>
 *                      <SVGAsset id="CloseIcon" />
 *                  </CustomAccordionClose>
 * 
 *                  ...
 *              </CustomAccordionContent>
 *          </CustomAccordion>
 *      </div>
 *  );
 * }
 */
export function CustomAccordionClose(props) {
    const controller = useCustomAccordion();

    if(!controller) {
        throw new Error('Unable to access "useCustomAccordion" hook. CustomAccordionClose must be a child of CustomAccordion');
    }

    const onClick = evt => {
        controller.close();

        if(props.onClick instanceof Function) {
            props.onClick(false);
        }
    }

    return (
        <div className={ `custom-accordion-button ${ props.className ?? "" }` } onClick={ onClick }>
            { props.children }
        </div>
    );
}